import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { ReactNode } from 'react';

import ButtonLink from '@/components/input/ButtonLink';
import Space from '@/components/layout/Space';
import ErrorHelperText from '@/components/output/ErrorHelperText';
import Label from '@/components/typography/Label';
import { Func } from '@/utils/ts';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    color: 'initial',
    textAlign: 'initial',
    textDecoration: 'none !important',
    outline: 'none !important',
    '&:focus > span': {
      borderColor: theme.colors.accent,
    },
  },
  label: {
    theme: theme.colors.primary,
  },
  optionBox: {
    flexShrink: 0,
    cursor: 'pointer',
    border: `1px solid ${theme.colors.secondary}`,
    backgroundColor: theme.colors.primaryBackground,
    boxSizing: 'border-box',
    borderRadius: '12px',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    '&:hover': {
      borderColor: theme.colors.accent,
    },
  },
  optionBoxDisabled: {
    cursor: 'unset',
    borderColor: theme.colors.secondary,
    '&:hover': {
      borderColor: theme.colors.secondary,
    },
  },
  titleBox: {
    justifyContent: 'center',
  },
}));

interface OptionSelectionProps {
  'data-test'?: string;
  startIcon?: ReactNode;
  title: ReactNode;
  label?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  onSelect?: Func;
}

const OptionSelection: React.FC<OptionSelectionProps> = ({
  startIcon,
  title,
  label,
  error,
  disabled,
  onSelect,
  'data-test': dataTest,
}) => {
  const classes = useStyles();
  return (
    <ButtonLink
      data-test={dataTest && `${dataTest}-link`}
      onClick={(): void | Promise<void> => onSelect?.()}
      className={classes.button}
    >
      <Space className={clsx(classes.optionBox, disabled && classes.optionBoxDisabled)}>
        {startIcon}
        <Space direction="vertical" size={0} className={classes.titleBox}>
          <span data-test={dataTest && `${dataTest}-title`}>{title}</span>
          {label && (
            <Label data-test={dataTest && `${dataTest}-label`} className={classes.label}>
              {label}
            </Label>
          )}
          {error && <ErrorHelperText data-test={dataTest && `${dataTest}-error`}>{error}</ErrorHelperText>}
        </Space>
      </Space>
    </ButtonLink>
  );
};

export default OptionSelection;
